import { useMemo, useState } from "react";
import { ChevronDown, Globe, Receipt, Layers, Landmark, Download } from "lucide-react";

export const COLS = [
  ["gross", "Gross Sales"],
  ["discount", "Discounts"],
  ["net", "Net Sales"],
  ["tax", "Sales Tax"],
  ["shipping", "Shipping"],
  ["total", "Net Total"],
];
export const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cell = (k, v) => (k === "discount" && v ? `−${money(v)}` : money(v));

const CHANNELS = [
  ["web", "Web", Globe, "text-sky-600"],
  ["offline", "Offline", Receipt, "text-amber-600"],
  ["all", "Total", Layers, "text-emerald-600"],
];

// Full breakdown table, always visible: Gross → Discounts → Net Sales → Sales Tax → Shipping → Net Total × Web / Offline / Total.
export function SummaryCards({ summary }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="sales-summary">
      <table className="w-full text-[11px] sm:text-sm table-fixed">
        <thead className="bg-zinc-50">
          <tr>
            <th className="text-left px-2 sm:px-3 py-2 text-[10px] uppercase tracking-wide text-zinc-500 font-semibold w-[28%]">Breakdown</th>
            {CHANNELS.map(([key, label, Icon, tone]) => {
              const s = summary?.[key] || {};
              return (
                <th key={key} className="text-right px-2 sm:px-3 py-2 font-semibold" data-testid={`sales-card-${key}`}>
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide ${tone}`}><Icon size={11} />{label}</span>
                  <div className="text-[9px] sm:text-[10px] font-normal text-zinc-400">{s.count || 0} {s.count === 1 ? "sale" : "sales"}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {COLS.map(([k, l]) => {
            const bold = k === "net" || k === "total";
            return (
              <tr key={k} className={k === "total" ? "bg-emerald-50/60" : k === "net" ? "bg-zinc-50" : ""} data-testid={`sales-summary-line-${k}`}>
                <td className={`px-2 sm:px-3 py-1.5 sm:py-2 ${bold ? "font-bold text-zinc-900" : "text-zinc-600"}`}>{l}</td>
                {CHANNELS.map(([key]) => (
                  <td key={key} className={`px-2 sm:px-3 py-1.5 sm:py-2 text-right tabular-nums ${bold ? "font-bold text-zinc-900" : k === "discount" ? "text-emerald-700" : "text-zinc-800"}`} data-testid={`sales-card-${key}-row-${k}`}>
                    {cell(k, summary?.[key]?.[k])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Sales-tax outlook: tax collected in the period, split by ship-to state for filing.
export function SalesTaxCard({ summary, byState, period }) {
  const [open, setOpen] = useState(false);
  const s = summary?.all || {};
  const rows = byState || [];
  const exportCsv = (e) => {
    e.stopPropagation();
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [["Period", `${period?.from || ""} to ${period?.to || ""}`], [], ["State", "Taxed sales", "Net sales", "Sales tax"],
      ...rows.map((r) => [r.state, r.count, r.net.toFixed(2), r.tax.toFixed(2)]),
      ["TOTAL", rows.reduce((a, r) => a + r.count, 0), Number(rows.reduce((a, r) => a + r.net, 0)).toFixed(2), Number(s.tax || 0).toFixed(2)]];
    const blob = new Blob([lines.map((l) => l.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `sales-tax-${period?.from || "report"}_${period?.to || ""}.csv` });
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60" data-testid="sales-tax-card">
      <div className="flex items-center gap-2 p-3">
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-0 flex items-center justify-between gap-2 text-left" data-testid="sales-tax-toggle">
          <div className="flex items-center gap-2 min-w-0">
            <Landmark size={16} className="text-amber-700 shrink-0" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-amber-800/80">Sales tax collected · {rows.length} {rows.length === 1 ? "state" : "states"}</div>
              <div className="text-lg font-bold text-zinc-900 tabular-nums" data-testid="sales-tax-total">{money(s.tax)}</div>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-right hidden sm:block">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Web / Offline</div>
              <div className="text-xs font-semibold tabular-nums text-zinc-700">{money(summary?.web?.tax)} / {money(summary?.offline?.tax)}</div>
            </div>
            <ChevronDown size={16} className={`text-amber-700 transition-transform ${open ? "rotate-180" : ""}`} />
          </div>
        </button>
        <button type="button" onClick={exportCsv} disabled={!rows.length} className="h-8 px-2.5 rounded-lg border border-amber-300 bg-white text-[11px] font-semibold text-amber-800 inline-flex items-center gap-1 disabled:opacity-40 print:hidden shrink-0" data-testid="sales-tax-export"><Download size={12} /> CSV</button>
      </div>
      {open && (
        <table className="w-full text-xs border-t border-amber-200" data-testid="sales-tax-by-state">
          <thead className="text-[10px] uppercase tracking-wide text-zinc-500">
            <tr><th className="text-left px-3 py-1.5">State</th><th className="text-right px-3 py-1.5">Taxed sales</th><th className="text-right px-3 py-1.5">Net sales</th><th className="text-right px-3 py-1.5">Tax</th></tr>
          </thead>
          <tbody className="divide-y divide-amber-100">
            {rows.map((r) => (
              <tr key={r.state} data-testid={`sales-tax-state-${r.state}`}>
                <td className="px-3 py-1.5 font-semibold">{r.state}</td><td className="px-3 py-1.5 text-right tabular-nums">{r.count}</td><td className="px-3 py-1.5 text-right tabular-nums">{money(r.net)}</td><td className="px-3 py-1.5 text-right tabular-nums font-semibold">{money(r.tax)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={4} className="px-3 py-3 text-center text-zinc-500">No sales tax collected in this period.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

const Badge = ({ paid }) => (
  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${paid ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{paid ? "Paid" : "Pending"}</span>
);

const Figures = ({ r, size = "text-xs" }) => (
  <dl className={`px-3 pb-3 grid grid-cols-2 gap-x-3 gap-y-1 ${size} border-t border-zinc-100 pt-2`}>
    {COLS.map(([k, l]) => (
      <div key={k} className={`flex justify-between ${k === "net" || k === "total" ? "font-semibold text-zinc-900" : "text-zinc-600"}`}><dt>{l}</dt><dd className="tabular-nums">{cell(k, r[k])}</dd></div>
    ))}
    <div className="flex justify-between text-zinc-600"><dt>Payment</dt><dd>{r.payment || "—"}</dd></div>
    <div className="flex justify-between text-zinc-600"><dt>Items</dt><dd>{r.item_count || 0}</dd></div>
  </dl>
);

// Phone view: searchable sale cards, Detailed (all figures shown) or Compact (tap to open). Desktop: the full table.
export function InvoiceList({ rows }) {
  const [q, setQ] = useState("");
  const [detailed, setDetailed] = useState(true);
  const [openId, setOpenId] = useState("");
  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return !t ? rows : rows.filter((r) => [r.customer, r.number, r.date, r.paid_at, r.payment, r.status, r.source].some((v) => String(v || "").toLowerCase().includes(t)));
  }, [rows, q]);
  return (
    <div className="space-y-2" data-testid="sales-invoices">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, invoice #, date…" className="w-full h-10 rounded-xl border border-zinc-200 px-3 text-sm print:hidden" data-testid="sales-invoice-search" />
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs text-zinc-500" data-testid="sales-invoice-count">{list.length} of {rows.length} sales</div>
        <div className="sm:hidden print:hidden flex rounded-full border border-zinc-200 bg-white p-0.5 text-[11px] font-semibold" data-testid="sales-view-toggle">
          {[[true, "Detailed"], [false, "Compact"]].map(([v, l]) => (
            <button key={l} type="button" onClick={() => setDetailed(v)} className={`px-3 h-7 rounded-full ${detailed === v ? "bg-zinc-900 text-white" : "text-zinc-600"}`} data-testid={`sales-view-${l.toLowerCase()}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="sm:hidden print:hidden space-y-2">
        {list.map((r) => {
          const isOpen = detailed || openId === r.id;
          return (
            <div key={r.id} className="rounded-2xl border border-zinc-200 bg-white" data-testid={`sales-row-${r.id}`}>
              <button type="button" onClick={() => !detailed && setOpenId(isOpen ? "" : r.id)} className="w-full p-3 text-left" data-testid={`sales-row-${r.id}-toggle`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-900 truncate">{r.customer || "—"}</div>
                    <div className="text-[11px] text-zinc-500 truncate">#{r.number} · {r.source === "invoice" ? "Offline" : "Web"} · ordered {r.date}{r.paid_at && r.paid_at !== r.date ? ` · paid ${r.paid_at}` : ""}</div>
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end gap-1">
                    <div className="text-sm font-bold tabular-nums">{money(r.total)}</div>
                    <div className="flex items-center gap-1"><Badge paid={r.paid} />{!detailed && <ChevronDown size={14} className={`text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />}</div>
                  </div>
                </div>
              </button>
              {isOpen && <Figures r={r} />}
            </div>
          );
        })}
        {!list.length && <div className="text-sm text-zinc-500 py-6 text-center">No sales match.</div>}
      </div>

      <div className="hidden sm:block print:block overflow-x-auto rounded-2xl border border-zinc-200 bg-white">
        <table className="w-full text-xs" data-testid="sales-table">
          <thead className="bg-zinc-50 text-zinc-500 uppercase tracking-wide text-[10px]">
            <tr>
              <th className="text-left p-1.5">Date</th><th className="text-left p-1.5">Paid</th><th className="text-left p-1.5">#</th><th className="text-left p-1.5">Customer</th><th className="text-left p-1.5">Channel</th><th className="text-left p-1.5">Status</th>
              {COLS.map(([k, l]) => <th key={k} className="text-right p-1.5 whitespace-nowrap">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} className="border-t border-zinc-100">
                <td className="p-1.5 whitespace-nowrap">{r.date}</td><td className="p-1.5 whitespace-nowrap">{r.paid_at || "—"}</td><td className="p-1.5">{r.number}</td><td className="p-1.5 max-w-[260px] truncate">{r.customer}</td>
                <td className="p-1.5">{r.source === "invoice" ? "Offline" : "Web"}</td><td className="p-1.5"><Badge paid={r.paid} /></td>
                {COLS.map(([k]) => <td key={k} className={`p-1.5 text-right tabular-nums whitespace-nowrap ${k === "net" || k === "total" ? "font-semibold" : ""}`}>{cell(k, r[k])}</td>)}
              </tr>
            ))}
            {!list.length && <tr><td colSpan={12} className="p-6 text-center text-sm text-zinc-500">No sales match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
